import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bookHref = (url, prefill) => {
  if (!url.startsWith('/book-a-call')) return url;
  const q = new URLSearchParams(url.split('?')[1] || '');
  Object.entries(prefill).forEach(([k, v]) => { if (v) q.set(k, String(v).trim()); });
  return `/book-a-call?${q.toString()}`;
};
const validEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((e || '').trim());

// Shown instead of a self-serve checkout for larger suppliers. Logs them so the team can chase.
export const SalesCallCard = ({ gate, company, email, name, source, staffCount, band, supplierSlug, testid = 'sales-call' }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const ctx = useRef({});
  ctx.current = { email, name, company, staff_count: staffCount || null, band, source, supplier_slug: supplierSlug };
  const log = (extra = {}) => {
    if (!validEmail(ctx.current.email)) return Promise.resolve();
    return axios.post(`${API}/public/sales-gate/stopped`, { ...ctx.current, email: ctx.current.email.trim(), ...extra }).catch(() => {});
  };
  useEffect(() => {
    const t = setTimeout(() => log(), 1200);
    return () => clearTimeout(t);
  }, [email, name, company, staffCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async () => {
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, { email, name, organisation: company, interest: 'sales_call', source });
      await log({ requested_call: true });
      setDone(true);
    } finally { setBusy(false); }
  };
  return (
    <div className="border-2 border-[#00A79D] bg-[#f0fbfa] rounded-xl p-6" data-testid={testid}>
      <p className="font-heading text-xl font-extrabold text-gray-900">Let's put together the right package</p>
      <p className="text-gray-600 mt-2 text-[15px] leading-relaxed">
        For companies with more than {gate?.max_staff || 5} staff we build a tailored package, from featured placement to campaigns with our care-provider audience.
        Book a meeting with our Sales team and we'll talk through what works for {company || 'you'}.
      </p>
      {gate?.sales_email && (
        <p className="mt-4 text-sm text-gray-700" data-testid={`${testid}-sales-email`}>
          Email Sales: <a href={`mailto:${gate.sales_email}`} className="font-semibold text-[#00857D] hover:underline">{gate.sales_email}</a>
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {gate?.booking_url ? (
          <a href={bookHref(gate.booking_url, { name, email, company, staff: staffCount || '' })} {...(gate.booking_url.startsWith('/book-a-call') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            data-testid={`${testid}-book-btn`} onClick={() => log({ clicked_book: true })}
            className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] font-semibold rounded-lg px-5 py-3 transition-colors">
            <CalendarDays className="w-4 h-4" /> Book a meeting with Sales
          </a>
        ) : done ? (
          <span className="inline-flex items-center gap-2 text-emerald-700 font-semibold" data-testid={`${testid}-requested`}><CheckCircle2 className="w-5 h-5" /> Thanks, we'll be in touch to arrange a call.</span>
        ) : (
          <button type="button" onClick={request} disabled={busy || !email} data-testid={`${testid}-request-btn`}
            className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] font-semibold rounded-lg px-5 py-3 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />} Request a call
          </button>
        )}
        {!validEmail(email) && !done && <span className="text-xs text-gray-500" data-testid={`${testid}-email-hint`}>Add your email above first.</span>}
      </div>
    </div>
  );
};
